import { ApiError } from "@nabble/sdk";

/** User-facing messages for the backend's stable error codes. */
const MESSAGES: Record<string, string> = {
  invalid_credentials: "Wrong email or password.",
  invalid_code: "That code isn't valid. Check that your device's clock is correct and try again.",
  locked: "Your account is temporarily locked after too many failed attempts.",
  no_login_in_progress: "Your sign-in expired. Please sign in again.",
  invalid_link: "This link isn't valid or has expired. Ask for a new one.",
  weak_password: "The password doesn't meet the security policy.",
  wrong_privacy_version: "The privacy notice has changed. Reload the page to see the current version.",
  email_taken: "That email is already in use.",
  role_name_taken: "A role with that name already exists.",
  system_role: "The Administrator role always has every permission and can't be changed.",
  collaborator_cannot_administer: "Collaborators can't manage the suite. Make the person an administrator instead.",
  unknown_organization: "That organization doesn't exist anymore. Reload the page.",
  organization_name_taken: "An organization with that name already exists.",
  organization_not_empty_or_default: "Only an organization without people can be deleted, and never the first one.",
  cannot_change_own_organization: "You can't change your own organization.",
  cannot_change_own_status: "You can't change your own status.",
  cannot_change_own_roles: "You can't change your own role.",
  cannot_anonymize_self: "You can't anonymize your own account.",
  user_not_active: "This user isn't active.",
  last_factor: "You must keep at least one second factor.",
  below_security_floor: "Some values are outside the allowed range.",
  roles_required: "Choose at least one role.",
  unknown_role: "One of those roles doesn't exist anymore. Reload the page.",
  role_not_assignable: "Only a suite administrator can give or take away roles that reach the whole suite.",
  role_in_use: "Someone holds only this role in an organization. Give them another role first.",
  roles_are_per_organization: "Roles are given in each organization. Use Organizations instead.",
  cannot_change_own_role: "You can't change your own roles.",
};

export const PASSWORD_VIOLATIONS: Record<string, string> = {
  "password.too-short": "It must be at least 12 characters long.",
  "password.too-long": "It's too long.",
  "password.blank": "It can't be blank.",
  "password.contains-email": "It can't contain your email.",
};

export function errorCode(err: unknown): string | null {
  return err instanceof ApiError ? err.code : null;
}

/** Human message for any error thrown by the apiClient (or the network). */
export function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.status === 429) return "Too many attempts in a row. Wait a moment and try again.";
    const known = MESSAGES[err.code];
    if (known) return known;
    if (err.status >= 500) return "The service isn't available right now. Try again in a few minutes.";
    return fallback;
  }
  if (err instanceof TypeError) return "We couldn't reach the server. Check your connection and try again.";
  return fallback;
}
